// Numeric band (age-range) helpers for the report builder's grouping editor.
// Mirrors Report::ReportGrouping#bands semantics: a null bound means the
// range is open on that side, rows with no numeric bound are ignored, and
// bands sort by lower bound.

export type Bound = number | null;

// Smallest gap between two distinct values of the banded field. 1 for integer
// domains, where 0-2 and 3-5 are contiguous and the interval between them
// holds nothing. 0 for continuous domains (decimals, currency), where the only
// contiguous arrangement is ranges that touch — 0-2 and 3-5 leave everything
// between 2 and 3 uncovered, and those rows fall out of the chart entirely
// because the generated CASE yields NULL.
export type BandStep = 0 | 1;

const DEFAULT_STEP: BandStep = 1;

export interface Band {
  min: Bound;
  max: Bound;
}

export interface BandWarning {
  type: "overlap" | "gap";
  a?: string;
  b?: string;
  range?: string;
}

export interface BandAdjustment {
  bandIndex: number;
  bound: "min" | "max";
  value: number;
}

const numericBound = (value: unknown): Bound =>
  value === "" || value == null || isNaN(Number(value)) ? null : Number(value);

export const normalizeBand = (raw: { min?: unknown; max?: unknown } | null | undefined): Band => ({
  min: numericBound(raw?.min),
  max: numericBound(raw?.max)
});

export function sortedBands(raw: Array<{ min?: unknown; max?: unknown }> | null | undefined): Band[] {
  return (raw || [])
    .map(normalizeBand)
    .filter((band) => band.min !== null || band.max !== null)
    .sort(
      (a, b) =>
        (a.min ?? -Infinity) - (b.min ?? -Infinity) ||
        (a.max ?? Infinity) - (b.max ?? Infinity),
    );
}

export function bandLabel(band: Band): string {
  if (band.min !== null && band.max !== null) return `${band.min}-${band.max}`;
  if (band.min !== null) return `${band.min}+`;
  return `≤${band.max}`;
}

// Interior gaps and overlaps between configured bands. Territory below the
// first band or above the last is intentionally unflagged — excluding
// out-of-range rows is the feature, not a mistake.
export function bandWarnings(
  raw: Array<{ min?: unknown; max?: unknown }> | null | undefined,
  step: BandStep = DEFAULT_STEP,
): BandWarning[] {
  const bands = sortedBands(raw);
  const warnings: BandWarning[] = [];

  for (let i = 1; i < bands.length; i++) {
    const prev = bands[i - 1];
    const next = bands[i];
    const prevMax = prev.max ?? Infinity;
    const nextMin = next.min ?? -Infinity;

    // prevMax + step is the first value the previous band doesn't claim, so
    // anything below it overlaps and anything above it leaves a hole. On a
    // continuous domain that collapses to "touching is the only clean fit".
    if (nextMin < prevMax + step) {
      warnings.push({ type: "overlap", a: bandLabel(prev), b: bandLabel(next) });
    } else if (nextMin > prevMax + step) {
      const from = prevMax + step;
      const to = nextMin - step;
      warnings.push({ type: "gap", range: from === to ? `${from}` : `${from}-${to}` });
    }
  }

  return warnings;
}

// Suggested min for a newly added band: one past the highest max already
// entered, so consecutive ranges chain (…-18 → 19-…). Null when no band has
// an upper bound to chain from.
export function nextBandMin(
  raw: Array<{ min?: unknown; max?: unknown }> | null | undefined,
  step: BandStep = DEFAULT_STEP,
): number | null {
  const maxes = (raw || [])
    .map((band) => normalizeBand(band).max)
    .filter((max): max is number => max !== null);

  return maxes.length ? Math.max(...maxes) + step : null;
}

// When a band's bound changes, clamp overlapping neighbors out of its way —
// the edited value is the freshest statement of intent. A neighbor is only
// adjusted when the clamp leaves it valid; a band the edited range swallows
// whole is left alone for the overlap warning, because silently rewriting or
// emptying a row that a typo collided with destroys more than it fixes.
export function resolveOverlaps(
  raw: Array<{ min?: unknown; max?: unknown }> | null | undefined,
  editedIndex: number,
  step: BandStep = DEFAULT_STEP,
): BandAdjustment[] {
  const bands = (raw || []).map(normalizeBand);
  const edited = bands[editedIndex];
  if (!edited || (edited.min === null && edited.max === null)) return [];

  const editedMin = edited.min ?? -Infinity;
  const editedMax = edited.max ?? Infinity;
  const adjustments: BandAdjustment[] = [];

  bands.forEach((band, bandIndex) => {
    if (bandIndex === editedIndex) return;
    if (band.min === null && band.max === null) return;

    const min = band.min ?? -Infinity;
    const max = band.max ?? Infinity;
    if (max < editedMin || min > editedMax) return;

    if (min < editedMin && editedMin - step >= min) {
      adjustments.push({ bandIndex, bound: "max", value: editedMin - step });
    } else if (max > editedMax && editedMax + step <= max) {
      adjustments.push({ bandIndex, bound: "min", value: editedMax + step });
    }
  });

  return adjustments;
}
